import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { z } from 'zod';
import { useUpdateProfile } from '../hooks/auth-actions.js';
import { describeError, mergeFormClasses } from './shared.js';
const schema = z.object({
    displayName: z
        .string()
        .trim()
        .min(1, 'Enter a display name.')
        .max(120, 'Display name is too long.'),
});
/**
 * Edit form for the profile fields a user can change about themselves. Only
 * `displayName` is editable today; the email/groups inputs are render
 * read-only above so the user can confirm what they're editing.
 */
export function ProfileForm({ user, onSaved, classNames, errorMessages, }) {
    const classes = mergeFormClasses(classNames);
    const action = useUpdateProfile();
    const [displayName, setDisplayName] = React.useState(user.displayName ?? '');
    const [validationError, setValidationError] = React.useState(null);
    const [savedAt, setSavedAt] = React.useState(null);
    // Reset the field if the parent swaps the user in (e.g. after refresh).
    React.useEffect(() => {
        setDisplayName(user.displayName ?? '');
    }, [user]);
    const error = describeError(action.error, errorMessages);
    const handleSubmit = async (event) => {
        event.preventDefault();
        const parsed = schema.safeParse({ displayName });
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter a display name.');
            return;
        }
        setValidationError(null);
        try {
            const next = await action.run({ displayName: parsed.data.displayName });
            setSavedAt(Date.now());
            onSaved?.(next);
        }
        catch {
            // Captured by hook.
        }
    };
    return (_jsxs("form", { className: classes.form, onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-profile-email", children: "Email" }), _jsx("input", { id: "auth-ui-profile-email", className: classes.input, type: "email", value: user.emailAddress ?? '', readOnly: true, disabled: true })] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-profile-displayName", children: "Display name" }), _jsx("input", { id: "auth-ui-profile-displayName", className: classes.input, type: "text", value: displayName, onChange: (event) => setDisplayName(event.target.value) }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null] }), error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, savedAt !== null && error === null ? (_jsx("p", { className: classes.success, role: "status", children: "Profile saved." })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: action.loading, children: action.loading ? 'Saving…' : 'Save profile' })] }));
}
//# sourceMappingURL=ProfileForm.js.map